/**
 * Shared voice + delivery helpers so Jet sounds like a teammate, not a brochure.
 */

const OPENERS = Object.freeze([
  'Got it —',
  'Quick take:',
  'Short version:',
  'Here’s the deal:',
]);

/**
 * Pick one string from a list. Pass a stable seed in tests.
 * @param {string[]} options
 * @param {number} [seed]
 */
export function pickVariant(options = [], seed = Date.now()) {
  const list = Array.isArray(options) ? options.filter(Boolean) : [];
  if (!list.length) return '';
  const n = Math.abs(Number(seed) || 0);
  return list[n % list.length];
}

/**
 * Optionally prefix a short human opener (deterministic when seed provided).
 * @param {string} text
 * @param {{ seed?: number, force?: boolean, chanceBucket?: number }} [opts]
 */
export function withJetOpener(text, opts = {}) {
  const body = String(text || '').trim();
  if (!body) return '';
  if (/^(got it|quick take|short version|here’s the deal)/i.test(body)) return body;
  const seed = opts.seed ?? Date.now();
  const bucket = Math.abs(Number(seed) || 0) % 100;
  const chanceBucket = opts.chanceBucket ?? 40; // ~40% of replies
  if (!opts.force && bucket >= chanceBucket) return body;
  const opener = pickVariant(OPENERS, seed);
  return `${opener} ${body.charAt(0).toLowerCase()}${body.slice(1)}`;
}

/**
 * Split a long reply into 1–3 chat bubbles on sentence boundaries.
 * @param {string} text
 * @param {{ maxLen?: number }} [opts]
 * @returns {string[]}
 */
export function splitJetBubbles(text, opts = {}) {
  const maxLen = opts.maxLen ?? 170;
  const raw = String(text || '').replace(/\s+/g, ' ').trim();
  if (!raw) return [];
  if (raw.length <= maxLen) return [raw];

  const sentences = raw.match(/[^.!?]+[.!?]+|[^.!?]+$/g) || [raw];
  const bubbles = [];
  let current = '';

  for (const sentence of sentences) {
    const piece = sentence.trim();
    if (!piece) continue;
    const next = current ? `${current} ${piece}` : piece;
    if (next.length <= maxLen) {
      current = next;
      continue;
    }
    if (current) bubbles.push(current);
    if (piece.length <= maxLen) {
      current = piece;
    } else {
      // Hard-wrap very long sentences.
      let rest = piece;
      while (rest.length > maxLen) {
        bubbles.push(rest.slice(0, maxLen).trim());
        rest = rest.slice(maxLen).trim();
      }
      current = rest;
    }
  }
  if (current) bubbles.push(current);

  return bubbles.slice(0, 3);
}

/**
 * Typing delay scales with message length (feels less instant / robotic).
 * @param {string} text
 */
export function jetTypingDelayMs(text) {
  const len = String(text || '').length;
  return Math.min(1600, Math.max(380, Math.round(len * 11)));
}

/**
 * Build chat message objects from a Jet answer (multi-bubble).
 * Links attach to the last bubble only.
 */
export function jetAnswerToMessages(answer = {}, nextId = () => `jet-${Date.now()}`) {
  const text = String(answer.text || '').trim();
  const links = Array.isArray(answer.links) ? answer.links : [];
  const bubbles = splitJetBubbles(text);
  if (!bubbles.length) {
    return [{ id: nextId(), from: 'jet', text: '…', links }];
  }
  return bubbles.map((bubble, index) => ({
    id: nextId(),
    from: 'jet',
    text: bubble,
    links: index === bubbles.length - 1 ? links : [],
  }));
}
